import * as React from 'react';
import * as DateFns from 'date-fns';
import {ReactNode} from "react";
import PaymentOptionPopup from './PaymentOptionPopup';
import {CardDetails} from "./NewCard";
import {BankAccountDetails} from "./NewBankAccount";
import {HatType} from "./Hat";
import Dropdown, {DropdownOption} from "./Dropdown";
import formatter from "../utils/formatter-util";

export interface BillToAccountDetails {
  emailAddress?: string;
  accountNumber?: string;
}

export interface LastFormOfPayment {
  confirmationNumber?: string;
  paymentTransactionId?: string;
  brandName: string;
  brand: string;
  expMonth: string;
  expYear: string;
  last4Digits: string;
  type: string;
  payPalPaymentId?: string;
  payerId?: string;
}

export interface PayPalPaymentDetails {
  payPalPaymentId?: string;
  payerId?: string;
}

export interface PaymentAppsInfo {
  displayName: string;
  transactionId: string;
  paymentToken: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  country?: string;
  phoneNumber?: string;
  emailAddress?: string;
  source?: string;
}

export interface BitPayDetails {
  paymentStatus: string;
  paymentCode: string;
}

export interface PaymentMethodValue {
  paymentType: string;
  lastFormOfPaymentDetails?: LastFormOfPayment;
  billToAccountDetails?: BillToAccountDetails;
  cardDetails?: CardDetails;
  bankDetails?: BankAccountDetails;
  payPalDetails?: PayPalPaymentDetails;
}

export interface PaymentMethodDetails {
  paymentType: string;
  lastFormOfPaymentDetails?: LastFormOfPayment;
  billToAccountDetails?: BillToAccountDetails;
  cardDetails?: CardDetails;
  bankDetails?: BankAccountDetails;
  payPalDetails?: PayPalPaymentDetails;
  paymentAppsInfo?: PaymentAppsInfo;
  bitPayDetails?: BitPayDetails;
}

interface PaymentMethodProps {
  name?: string;
  accountNumber?: string;
  emailAddress?: string;
  ebillEnrolled?: boolean;
  value?: PaymentMethodValue;
  options?: PaymentMethodValue[];
  id?: string;
  label?: string;
  text?: string;
  cardExpiredHatText?: string;
  cardExpiringSoonHatText?: string;
  placeHolder?: string;
  required?: boolean;
  readOnly?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  hat?: HatType[];
  onSelect: (details: PaymentMethodDetails) => void;
  paymentEndingInLabel?: string;
  paymentOptionTexts?: any;
  eligibleMethods: string[];
  lastFormOfPayment?: LastFormOfPayment;
  payPalPaymentDetails?: any;
  payPalStatus?: 'RESET' | 'VALIDATED';
  cardChargeBack?: boolean;
  eftChargeBack?: boolean;
  paymentFlow?: string;
  disabled?: boolean;
  hideSavePaymentInfo?: boolean;
  cardChargeBackMessage?: ReactNode;
  eftChargeBackMessage?: ReactNode;
  toolTip?: ReactNode;
}

interface PaymentMethodState {
  value?: PaymentMethodValue;
  prevValue?: PaymentMethodValue;
  paymentOptions: DropdownOption[];
  displayNewPaymentPopup: boolean;
  paymentPopupType?: string;
  hat?: HatType[];
  payPalValidated?: boolean;
}

export default function PaymentMethod(props: PaymentMethodProps) {
  const {
    name = 'paymentmethod',
    id = 'paymentmethod',
    label = 'Payment Method',
    text = 'Select Method',
    cardExpiredHatText = 'The saved card on this account has expired.',
    cardExpiringSoonHatText = 'This card is expiring soon.',
    paymentOptionTexts = {
      NEWCARD: 'New Card',
      NEWEFT: 'New Bank Account',
      PAYPAL: 'PayPal',
      BITCOIN: 'Bitcoin',
      APPLE: 'Apple Pay',
      GOOGLE: 'Google Pay',
      BILLTOACCOUNT: 'Bill To Account'
    },
    hideSavePaymentInfo = false
  } = props;

  let lastFormOfPaymentOption: null | DropdownOption = null;

  props.eligibleMethods.forEach((methodType: string) => {
    if (
        methodType === 'LASTFORMOFPAYMENT' &&
        props.lastFormOfPayment &&
        ['CARD', 'EFT'].includes(props.lastFormOfPayment.type)
    ) {
      lastFormOfPaymentOption = {
        text: props.lastFormOfPayment.last4Digits
            ? formatter({
              type:
                  props.lastFormOfPayment.type === 'CARD'
                      ? 'card'
                      : 'bankaccount',
              format: props.lastFormOfPayment.brand,
              value: `000000${props.lastFormOfPayment.last4Digits}`
            })
            : '',
        value: 'LASTFORMOFPAYMENT',
        iconPath: `/cms-files/paymentlogos/${props.lastFormOfPayment.brand}.svg`,
        additionalText:
            props.lastFormOfPayment.type === 'CARD'
                ? `${
                    props.lastFormOfPayment.expMonth
                    } / ${props.lastFormOfPayment.expYear.slice(
                props.lastFormOfPayment.expYear.length - 2
                )}`
                : ''
      };
    }
  });

  const cardOptions:DropdownOption[]=[];
  props.eligibleMethods.forEach((methodType: string) => {
    if (methodType === 'OPTIONS') {
      props.options && props.options.map((paymentMethod: PaymentMethodValue) => {
        if(paymentMethod.paymentType.startsWith('OPTIONS') && paymentMethod.cardDetails){
          const paymentOption: DropdownOption = {
            text: formatter({
              type: 'card',
              format: paymentMethod.cardDetails.cardBrand,
              value: `000000${paymentMethod.cardDetails.cardNumber}`
            }),
            value: `${paymentMethod.paymentType}`,
            iconPath: `/cms-files/paymentlogos/${paymentMethod.cardDetails.cardBrand}.svg`,
            additionalText: `${
                paymentMethod.cardDetails.expMonth
                } / ${paymentMethod.cardDetails.expYear.slice(
                paymentMethod.cardDetails.expYear.length - 2
            )}`
          };
          cardOptions.push(paymentOption);
        }
      });
    }
  });

  const sortPaymentMethodOptions = [
    'NEWCARD',
    'NEWEFT',
    'APPLE',
    'GOOGLE',
    'PAYPAL',
    'BITCOIN',
    'BILLTOACCOUNT'
  ];
  let paymentMethodOptions:
      | undefined
      | DropdownOption[] = sortPaymentMethodOptions
      .filter((methodType: string) => props.eligibleMethods.includes(methodType))
      .map((methodType: string) => {
        return {
          text: paymentOptionTexts[methodType],
          value: methodType,
          iconPath: `/cms-files/paymentlogos/${methodType}.svg`
        };
      });
  let defaultValue: any;
  if (lastFormOfPaymentOption) {
    paymentMethodOptions.unshift(lastFormOfPaymentOption);
    defaultValue = {
      paymentType: props.paymentFlow === 'AUTOPAY' ? '' : 'LASTFORMOFPAYMENT',
      lastFormOfPaymentDetails: props.lastFormOfPayment
    };
  } else if (props.lastFormOfPayment) {
    defaultValue = {
      paymentType:
          props.paymentFlow === 'AUTOPAY' ? '' : props.lastFormOfPayment.type
    };
  }
  if(cardOptions.length){
    paymentMethodOptions = cardOptions.concat(paymentMethodOptions);
  }
  const [state, setState] = React.useState<PaymentMethodState>({
    displayNewPaymentPopup: false,
    paymentOptions: paymentMethodOptions,
    value: props.value || defaultValue
  });

  const newPaymentAdded = (details: PaymentMethodValue) => {
    let endingInText: any;
    let logo: any;
    let number: any = '';
    if (details.paymentType.endsWith('CARD') && details.cardDetails) {
      number = details.cardDetails.cardNumber;
      endingInText = formatter({
        type: 'card',
        format: details.cardDetails.cardBrand,
        value: number
      });
      logo = details.cardDetails.cardBrand;
    } else if (details.paymentType.endsWith('EFT') && details.bankDetails) {
      number = details.bankDetails.accountNumber;
      endingInText = formatter({ type: 'bankaccount', value: number });
      logo = details.bankDetails.bankCode;
    } else if (
        details.paymentType.endsWith('PAYPAL') &&
        details.payPalDetails &&
        props.payPalPaymentDetails
    ) {
      endingInText = props.payPalPaymentDetails.payerEmail;
      logo = `PAYPAL`;
    } else if (
        details.paymentType.endsWith('BILLTOACCOUNT') &&
        details.billToAccountDetails &&
        details.billToAccountDetails.accountNumber
    ) {
      endingInText = details.billToAccountDetails.accountNumber;
      logo = `BILLTOACCOUNT`;
    }

    if (endingInText) {
      const newPaymentOptions: DropdownOption[] = [
        ...state.paymentOptions
      ].filter(
          (methodType: DropdownOption) =>
              !methodType.iconPath ||
              methodType.iconPath
                  .substring(methodType.iconPath.lastIndexOf('/') + 1)
                  .slice(0, -4)
                  .toUpperCase() !== logo ||
              methodType.text.slice(-4) !== number.slice(-4)
      );
      const additionalText = ['CARD'].includes(details.paymentType)
          ? details.cardDetails &&
          `${
              details.cardDetails.expMonth
              } / ${details.cardDetails.expYear.slice(
              details.cardDetails.expYear.length - 2
          )}`
          : '';
      newPaymentOptions.unshift({
        text: endingInText,
        value: `NEWPAYMENT${details.paymentType}${number.slice(-4)}${additionalText}`,
        iconPath: `/cms-files/paymentlogos/${logo}.svg`,
        additionalText
      });
      setState((prevState: PaymentMethodState) => ({
        ...prevState,
        paymentOptions: newPaymentOptions,
        displayNewPaymentPopup: false,
        value: {
          ...prevState.value,
          ...details,
          paymentType: `NEWPAYMENT${details.paymentType}${number.slice(-4)}${additionalText}`
        }
      }));
    }
  };

  const newPaymentRemoved = (paymentType: string) => {
    const newPaymentOptions: DropdownOption[] = [
      ...state.paymentOptions
    ].filter(
        (methodType: DropdownOption) =>
            methodType.value !== `NEWPAYMENT${paymentType}`
    );
    const detailsReset: any = {};
    switch (paymentType) {
      case 'PAYPAL':
        detailsReset.payPalDetails = undefined;
        break;
      default:
        // do-nothing
    }
    setState((prevState: PaymentMethodState) => ({
      ...prevState,
      paymentOptions: newPaymentOptions,
      displayNewPaymentPopup: false,
      value: { ...prevState.value, ...detailsReset, paymentType }
    }));
  };

  React.useEffect(() => {
    if (state.value) {
      props.onSelect(state.value);
    }
  }, [state.value]);

  React.useEffect(() => {
    if (
        props.value &&
        props.value !== state.value &&
        JSON.stringify(props.value) !== JSON.stringify(state.value)
    ) {
      if (
          props.value.paymentType.startsWith('NEWPAYMENT') &&
          (!state.value || state.value.paymentType !== props.value.paymentType)
      ) {
        newPaymentAdded({
          ...props.value,
          paymentType: props.value.paymentType.substr(10)
        });
      } else if (props.value.paymentType === 'DEFAULTPAYMENT') {
        setState({ ...state, value: defaultValue });
      } else {
        setState({ ...state, value: props.value });
      }
    }
  }, [props.value]);

  React.useEffect(() => {
    if (props.payPalStatus === 'VALIDATED') {
      newPaymentAdded({
        paymentType: 'PAYPAL',
        payPalDetails: props.payPalPaymentDetails
      });
    } else if (
        props.payPalStatus === 'RESET' &&
        state.paymentOptions.find(
            (methodType: DropdownOption) =>
                methodType.value === `NEWPAYMENTPAYPAL`
        )
    ) {
      newPaymentRemoved('PAYPAL');
    }
  }, [props.payPalStatus]);

  React.useEffect(()=>{
    if(paymentMethodOptions && paymentMethodOptions !== state.paymentOptions){
      const paymentOptions = [...paymentMethodOptions];
      setState((prevState:PaymentMethodState) => ({
        ...prevState,
        paymentOptions
      }))
    }
  }, [props.options]);

  let cardExpiredHat;
  let cardExpiredHatType: undefined | 'error' | 'warning' | 'success' | 'info';
  if (
      state.value &&
      state.value.paymentType === 'LASTFORMOFPAYMENT' &&
      props.lastFormOfPayment
  ) {
    const diffDays = DateFns.differenceInDays(
        DateFns.lastDayOfMonth(
            new Date(
                `${props.lastFormOfPayment.expMonth}/01/${props.lastFormOfPayment.expYear}`
            )
        ),
        new Date()
    );
    if (diffDays <= 0) {
      cardExpiredHat = <>{cardExpiredHatText}</>;
      cardExpiredHatType = 'error';
    } else if (diffDays <= 15) {
      cardExpiredHat = <>{cardExpiringSoonHatText}</>;
      cardExpiredHatType = 'warning';
    }
  }

  if (props.cardChargeBack && props.eftChargeBack) {
      // show a section messaging
    } else if (props.cardChargeBack && props.cardChargeBackMessage) {
      cardExpiredHat = props.cardChargeBackMessage;
      cardExpiredHatType = 'info';
    } else if (props.eftChargeBack && props.eftChargeBackMessage) {
      cardExpiredHat = props.eftChargeBackMessage;
      cardExpiredHatType = 'info';
    }

  return (
      <>
        <Dropdown
            disabled={props.disabled}
            readOnly={props.readOnly}
            name={name}
            id={id}
            label={label}
            noOptionSelectedText={text}
            required={props.required}
            value={state.value && state.value.paymentType}
            focus={props.focus}
            hasError={props.hasError}
            errors={props.errors}
            theme={props.theme}
            hat={
              cardExpiredHatType && [
                {
                  type: cardExpiredHatType,
                  message: cardExpiredHat
                }
              ]
            }
            onChange={(selectedOption: DropdownOption) => {
              if (
                  selectedOption.value === 'NEWCARD' ||
                  selectedOption.value === 'BILLTOACCOUNT' ||
                  selectedOption.value === 'NEWEFT'
              ) {
                setState((prevState: PaymentMethodState) => ({
                  ...prevState,
                  displayNewPaymentPopup: true,
                  paymentPopupType: selectedOption.value,
                  value: { ...prevState.value, paymentType: selectedOption.value },
                  prevValue: prevState.value
                }));
              }else if(
                  selectedOption.value.startsWith('OPTIONS') &&
                  state.value &&
                  selectedOption.value !== state.value.paymentType
              ){
                setState({
                  ...state,
                  displayNewPaymentPopup: false,
                  value: {
                    paymentType: selectedOption.value,
                    ...props.options && props.options.filter((option:PaymentMethodValue)=>option.paymentType===selectedOption.value)[0]
                  }
                })
              }else {
                setState((prevState: PaymentMethodState) => ({
                  ...prevState,
                  displayNewPaymentPopup: false,
                  value: { ...prevState.value, paymentType: selectedOption.value }
                }));
              }
            }}
            options={state.paymentOptions.filter(
                (opt: DropdownOption) =>
                    props.eligibleMethods.includes(opt.value) ||
                    opt.value.startsWith('NEWPAYMENT') ||
                    opt.value.startsWith('OPTIONS')
            )}
        />
        {state.displayNewPaymentPopup && state.paymentPopupType && (
            <PaymentOptionPopup
                hideSavePaymentInfo={hideSavePaymentInfo}
                emailAddress={props.emailAddress}
                accountNumber={props.accountNumber}
                ebillEnrolled={props.ebillEnrolled}
                paymentType={state.paymentPopupType}
                onOk={(details: PaymentMethodValue) => {
                  newPaymentAdded(details);
                }}
                onClose={() =>
                    setState({
                      ...state,
                      displayNewPaymentPopup: false,
                      value: state.prevValue
                    })
                }
                toolTip={props.toolTip}
            />
        )}
      </>
  );
}
